import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { ArrowLeft, ShoppingCart } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export default function CategoryCardsPage() {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const [cards, setCards] = useState([]);
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchCategoryCards();
  }, [categoryId]);

  const fetchCategoryCards = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/public/digital-cards/category/${categoryId}`);
      const data = await response.json();
      
      console.log('Category API Response:', data);
      console.log('Category ID from URL:', categoryId);
      console.log('Category found:', data.category);
      console.log('Cards found:', data.cards);
      
      setCards(data.cards || []);
      setCategory(data.category);
      setLoading(false);
    } catch (err) {
      console.error('Failed to fetch cards:', err);
      setError('Failed to load cards');
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 py-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy mx-auto"></div>
            <p className="text-gray-600 mt-4">Loading cards...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !category) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 py-20">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-navy mb-4">Category Not Found</h2>
              <p className="text-gray-600 mb-6">
                {error || 'The category you are looking for does not exist or has been removed.'}
              </p>
              <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg mb-6 text-left">
                <p className="text-sm text-gray-700"><strong>Category ID:</strong> {categoryId}</p>
                <p className="text-sm text-gray-700 mt-2"><strong>Problem:</strong> This category doesn't exist in the database.</p>
                {cards && cards.length > 0 && (
                  <p className="text-sm text-red-600 mt-2 font-semibold">
                    ⚠️ However, {cards.length} card(s) are assigned to this missing category!
                  </p>
                )}
              </div>
            </div>

            {/* Show orphaned cards if they exist */}
            {cards && cards.length > 0 && (
              <div className="mb-8">
                <h3 className="text-xl font-bold text-navy mb-4">Orphaned Cards (Need Reassignment)</h3>
                <p className="text-sm text-gray-600 mb-4">
                  These cards are assigned to the missing category. Please reassign them in the Admin Panel.
                </p>
                <div className="grid gap-4">
                  {cards.map((card) => (
                    <div key={card.id} className="bg-white border-2 border-red-300 rounded-lg p-4">
                      <div className="flex items-start gap-4">
                        {card.hero_image && (
                          <img
                            src={`${BACKEND_URL}${card.hero_image}`}
                            alt={card.name}
                            className="w-24 h-24 object-cover rounded-lg"
                          />
                        )}
                        <div className="flex-1">
                          <h4 className="font-bold text-navy mb-1">{card.name}</h4>
                          <p className="text-sm text-gray-600 mb-2">{card.short_description}</p>
                          <p className="text-xs text-red-600 font-semibold">
                            Card ID: {card.id}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            Go to Admin Panel → Digital Cards → Edit this card → Reassign to correct category
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="text-center">
              <Button onClick={() => navigate('/digital-cards')} className="bg-navy text-white">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Categories
              </Button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <Button 
            onClick={() => navigate('/digital-cards')}
            variant="ghost"
            className="mb-6 text-navy hover:text-navy/80"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Categories
          </Button>
          
          <div className="text-center">
            <h1 className="text-5xl lg:text-6xl font-extrabold text-navy mb-6">
              {category.name}
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-4">
              {category.description}
            </p>
            <div className="text-2xl font-bold text-green-600">
              Starting from ${category.starting_from}
            </div>
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          {cards.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {cards.map((card) => (
                <div 
                  key={card.id} 
                  className="bg-white rounded-[2rem] overflow-hidden shadow-sm hover:shadow-lg transition-shadow"
                >
                  {/* Card Image */}
                  <div className="aspect-[4/3] overflow-hidden bg-gray-100">
                    {card.hero_image ? (
                      <img
                        src={`${BACKEND_URL}${card.hero_image}`}
                        alt={card.name}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gray-200">
                        <span className="text-gray-400">No Image</span>
                      </div>
                    )}
                  </div>

                  {/* Card Details */}
                  <div className="p-6">
                    {/* Available Colours & Prices */}
                    {card.color_variants && card.color_variants.length > 0 && (
                      <div className="mb-3">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          {card.color_variants.map((variant) => (
                            <div key={variant.colour_id} className="flex items-center gap-1 bg-gray-50 px-2 py-1 rounded-lg border border-gray-200">
                              <div
                                className="w-4 h-4 rounded-full border-2 border-gray-300"
                                style={{ backgroundColor: variant.colour_code }}
                              />
                              <span className="text-xs font-semibold text-gray-700">{variant.colour_name}</span>
                              <span className="text-xs font-bold text-navy ml-1">${variant.price}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <h3 className="text-xl font-bold text-navy mb-2">{card.name}</h3>
                    <p className="text-sm text-gray-600 mb-4 line-clamp-2">{card.short_description}</p>
                    
                    <Button 
                      onClick={() => navigate(`/customize-card/${card.id}`)}
                      className="w-full bg-navy text-white hover:bg-navy/90 rounded-full py-3 flex items-center justify-center gap-2"
                    >
                      <ShoppingCart className="w-4 h-4" />
                      Order Now
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-white rounded-2xl">
              <div className="text-gray-400 mb-4">
                <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-700 mb-2">No Cards Available</h3>
              <p className="text-gray-600 mb-6">
                There are no cards in this category yet. Check back soon!
              </p>
              <Button 
                onClick={() => navigate('/digital-cards')}
                className="bg-navy text-white hover:bg-navy/90"
              >
                Browse Other Categories
              </Button>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
